/**
 * The entry point into policy-version comparison. Ported from the #5 prototype's
 * CriteriaIndexPage, which was reacted to at full length (124 rows) precisely because the
 * whole question about this page is whether it stays readable.
 *
 * One addition the prototype did not have: **stale badges**, since eligibility is standing
 * and a new version stales earlier grants.
 *
 * Two things that were here and are gone:
 *
 * - **Bulk actions per row** (Approve / Disregard / Clear / Re-affirm as icon buttons in an
 *   "All versions" column), added on #7 and removed 2026-09-21 at Gareth's request. Judging
 *   a whole criterion without opening it was the wrong affordance on a worklist whose point
 *   is reading the diff; the same actions remain on the comparison page, where the rubric
 *   is in front of you. OBSOLETE criteria (#12) are still an editorial call made there, not
 *   a rule in the schema, which is why obsolete rows are never filtered out by default.
 * - The threshold knob: #9 killed the threshold.
 */
import React from 'react';
import {
  Alert,
  Box,
  Chip,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Slider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PushPinIcon from '@mui/icons-material/PushPin';
import SearchIcon from '@mui/icons-material/Search';
import { useMemo, useState } from 'react';
import { CriterionEligibilitySummary, EligibilityProgress } from '../../../types';
import { formatDate } from './shared';

type SortKey = keyof Pick<
  CriterionEligibilitySummary,
  | 'criterionName'
  | 'versionsWithLabels'
  | 'versionsToJudge'
  | 'labelsAtStake'
  | 'labelsTotal'
  | 'lowestSimilarity'
>;

const COLUMNS: { key: SortKey; label: string; numeric: boolean; hint?: string }[] = [
  { key: 'criterionName', label: 'Criterion', numeric: false },
  {
    key: 'versionsWithLabels',
    label: 'Versions with labels',
    numeric: true,
    hint:
      "Versions carrying labels, out of every version in this criterion's lineage. Versions with no labels can never be ground truth, " +
      'so they never need looking at. Most rows read "2/7" or "2/17" — which is why this queue is tractable at all.',
  },
  {
    key: 'versionsToJudge',
    label: 'To judge',
    numeric: true,
    hint: 'Labelled versions nobody has ruled on yet',
  },
  {
    key: 'labelsAtStake',
    label: 'Labels at stake',
    numeric: true,
    hint:
      'Labels recovered as eval ground truth if this criterion is judged, out of every label it carries. ' +
      'A gap means some labels already sit on the current version and were never at risk — "415/642" is 415 to win, 227 already safe. ' +
      'Most rows read "X/X" because the current version carries no labels of its own.',
  },
  {
    key: 'labelsTotal',
    label: 'Total labels',
    numeric: true,
    hint: 'Every label this criterion carries, across every version, regardless of eligibility.',
  },
  {
    key: 'lowestSimilarity',
    label: 'Change',
    numeric: true,
    hint:
      'How much the most-changed unjudged version differs from the current one. A longer bar means more rewriting, so a longer read. ' +
      'It is NOT a safety score: a barely-changed version can still forbid what was previously credited — the 0.996 rows in this table include two known cases.',
  },
];

/**
 * How much rewriting the most-changed unjudged version carries — an **effort** signal.
 *
 * Three deliberate choices, each guarding against reading this as a safety score:
 *
 * - **The bar is 1 − cosine**, so a longer bar means more rewriting and a longer read.
 *   Drawing the similarity itself would make 0.997 a long, satisfying bar and invite
 *   "approve without looking".
 * - **Continuous grey, no bands.** Bands would draw a cut-off on screen, and #9 measured
 *   that no cut-off separates equivalent from revised on this corpus.
 * - **Lowest, not mean.** The worst unjudged version is the one that sets the reading time.
 *
 * Fixed 0.75–1.00 axis, which is the observed range, so rows are comparable at a glance.
 */
const CHANGE_AXIS_FLOOR = 0.75;

const ChangeCell = ({
  lowestSimilarity,
  scored,
}: {
  lowestSimilarity: number | null;
  scored: number;
}) => {
  if (lowestSimilarity === null) {
    return (
      <Tooltip title="No cached embedding for any unjudged version, so no similarity. Absence is shown rather than a zero, which would read as 'completely different'.">
        <Typography variant="caption" color="text.disabled">
          —
        </Typography>
      </Tooltip>
    );
  }

  const change = Math.min(Math.max((1 - lowestSimilarity) / (1 - CHANGE_AXIS_FLOOR), 0), 1);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.75 }}>
        <Typography
          variant="body2"
          sx={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}
        >
          {lowestSimilarity.toFixed(3)}
        </Typography>
        {scored > 1 ? (
          <Typography variant="caption" color="text.disabled">
            ({scored})
          </Typography>
        ) : null}
      </Box>
      <Box sx={{ height: 3, bgcolor: 'grey.200', borderRadius: 2, mt: 0.25 }}>
        <Box
          sx={{
            width: `${Math.max(change * 100, 2)}%`,
            height: '100%',
            bgcolor: 'grey.600',
            borderRadius: 2,
          }}
        />
      </Box>
    </Box>
  );
};

/**
 * Which of a criterion's versions the search text matched by policy UUID.
 *
 * Exported for tests. Empty unless the query looks like it is hunting an id — a two-letter
 * name fragment would otherwise "match" a UUID containing those letters and stamp a
 * meaningless badge on half the table. Four characters is enough to make an accidental hit
 * unlikely while still allowing a short prefix.
 */
export const MIN_ID_QUERY_LENGTH = 4;

/**
 * The Change column's value as a percentage — cosine similarity times 100.
 *
 * The filter bounds the number the column actually shows: a row reading 0.952 is 95.2%, so
 * an upper bound of 87.5% hides it. An earlier version bounded 1 − cosine instead, which
 * looked broken because setting 87.5% left every 0.9-something row on screen — their
 * *change* was only a few percent.
 */
export const similarityPercent = (lowestSimilarity: number): number =>
  // Rounded, not raw. Binary floating point makes `0.875 * 100` land at 87.49999999999999,
  // so a row sitting exactly on a bound would fall outside it. Four decimals is far finer
  // than the 0.1% step the slider offers.
  Math.round(lowestSimilarity * 1e6) / 1e4;

/** The slider spans the full percentage range. */
export const CHANGE_RANGE_MAX = 100;

/**
 * Whether a row falls inside the similarity range.
 *
 * A row with no similarity has no value to compare, so it cannot satisfy a bound — it is
 * excluded whenever the filter is narrowed, and the UI reports how many that removes rather
 * than dropping them silently. 43 of 124 rows are in that state, because they have nothing
 * left to judge.
 */
export const matchesChangeRange = (
  row: CriterionEligibilitySummary,
  [min, max]: [number, number],
  axisMax: number = CHANGE_RANGE_MAX
): boolean => {
  const filtering = min > 0 || max < axisMax;
  if (!filtering) return true;
  if (row.lowestSimilarity === null) return false;
  const similarity = similarityPercent(row.lowestSimilarity);
  return similarity >= min && similarity <= max;
};

export const matchingVersions = (row: CriterionEligibilitySummary, query: string): number[] => {
  const needle = query.trim().toLowerCase();
  if (needle.length < MIN_ID_QUERY_LENGTH) return [];
  return row.policyVersions
    .filter(version => version.policyId.toLowerCase().includes(needle))
    .map(version => version.version)
    .sort((a, b) => a - b);
};

const Stat = ({ value, label }: { value: string; label: string }) => (
  <Box>
    <Typography sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1.15 }}>{value}</Typography>
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
      {label}
    </Typography>
  </Box>
);

interface Props {
  summaries: CriterionEligibilitySummary[];
  progress: EligibilityProgress | null;
  onOpen: (criteriaId: string) => void;
}

const CriteriaIndex = ({ summaries, progress, onOpen }: Props) => {
  const [sortKey, setSortKey] = useState<SortKey>('labelsAtStake');
  const [descending, setDescending] = useState(true);
  const [query, setQuery] = useState('');

  // Held as the full range so the filter starts inactive.
  const [changeRange, setChangeRange] = useState<[number, number]>([0, CHANGE_RANGE_MAX]);
  const changeFilterActive = changeRange[0] > 0 || changeRange[1] < CHANGE_RANGE_MAX;

  const rows = useMemo(() => {
    // Matches the criterion name, the criterion's own UUID, or any of its policy version
    // UUIDs. Both sides are lowercased: UUIDs pasted from psql or a notebook may be upper
    // case while the stored ones are not. Substring rather than equality, so a fragment of
    // a UUID works as well as the whole thing.
    const needle = query.trim().toLowerCase();
    const matchesQuery = (row: CriterionEligibilitySummary) =>
      !needle ||
      row.criterionName.toLowerCase().includes(needle) ||
      row.criteriaId.toLowerCase().includes(needle) ||
      matchingVersions(row, query).length > 0;

    const filtered = summaries.filter(
      row => matchesQuery(row) && matchesChangeRange(row, changeRange)
    );
    const sorted = [...filtered].sort((a, b) => {
      if (sortKey === 'criterionName') return a.criterionName.localeCompare(b.criterionName);
      // lowestSimilarity is nullable, and `null - null` is NaN, which leaves the
      // comparator inconsistent and the order arbitrary. Rows with no similarity sort
      // last in whichever direction is active, so reversing cannot float them to the top.
      const left = a[sortKey];
      const right = b[sortKey];
      if (left === null && right === null) return 0;
      if (left === null) return descending ? -1 : 1;
      if (right === null) return descending ? 1 : -1;
      return (left as number) - (right as number);
    });
    return descending && sortKey !== 'criterionName' ? sorted.reverse() : sorted;
  }, [summaries, sortKey, descending, query, changeRange]);

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) setDescending(value => !value);
    else {
      setSortKey(key);
      setDescending(key !== 'criterionName');
    }
  };

  const maxAtStake = Math.max(...summaries.map(row => row.labelsAtStake), 1);

  // Denominator is every non-current version carrying labels -- fixed, so it does not
  // shrink when a version is ruled out. Using at-stake + recovered would make the
  // percentage climb just from excluding things, which is the opposite of progress.
  const recoveredShare =
    progress && progress.labelsRecoverable > 0
      ? (100 * progress.labelsRecovered) / progress.labelsRecoverable
      : 0;

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 1 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Policy Eligibility
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pick a criterion to compare its policy versions and decide which are equivalent enough for
          their labels to serve as eval ground truth.
        </Typography>
        {progress ? (
          <Box sx={{ display: 'flex', gap: 5, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <Stat value={String(progress.criteriaTotal)} label="criteria" />
            <Stat value={String(progress.versionsToJudge)} label="to judge" />
            <Stat value={progress.labelsAtStake.toLocaleString()} label="labels at stake" />
            <Stat value={progress.labelsRecovered.toLocaleString()} label="labels recovered" />
            <Stat
              value={`${progress.labelsEligible.toLocaleString()} / ${progress.labelsTotal.toLocaleString()}`}
              label="eligible now"
            />
            {progress.staleVersions > 0 ? (
              <Stat value={String(progress.staleVersions)} label="stale grants" />
            ) : null}
          </Box>
        ) : null}

        {/* Both filters on one row. The slider spans the full 0-100% track; real change on
            this corpus tops out around 24%, so the useful handles sit in the left quarter. */}
        <Box
          sx={{
            mt: 3,
            display: 'flex',
            alignItems: 'flex-start',
            columnGap: 3,
            rowGap: 2,
            flexWrap: 'wrap',
          }}
        >
          <TextField
            size="small"
            placeholder="Filter by name, criterion ID or policy ID…"
            value={query}
            onChange={event => setQuery(event.target.value)}
            sx={{ minWidth: 300, flex: '1 1 300px' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ minWidth: 300, flex: '1 1 300px' }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Only show criteria with a Change value between{' '}
              <Box component="span" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                {changeRange[0].toFixed(1)}% and {changeRange[1].toFixed(1)}%
              </Box>
            </Typography>
            <Slider
              size="small"
              value={changeRange}
              min={0}
              max={CHANGE_RANGE_MAX}
              step={0.1}
              valueLabelDisplay="auto"
              valueLabelFormat={value => `${value}%`}
              marks={[
                { value: 0, label: '0%' },
                { value: 50, label: '50%' },
                { value: CHANGE_RANGE_MAX, label: '100%' },
              ]}
              onChange={(_event, value) => setChangeRange(value as [number, number])}
              sx={{ mt: 0.5 }}
            />
          </Box>
          {/* Always mounted, only hidden. Rendering this conditionally made the page jump
              while dragging: appearing mid-drag shrank the flex row, which shrank the
              slider, which moved the handle out from under the cursor. `visibility` keeps
              the space reserved so no drag changes the layout. */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flex: '0 0 auto',
              visibility: changeFilterActive ? 'visible' : 'hidden',
              pointerEvents: changeFilterActive ? 'auto' : 'none',
            }}
            aria-hidden={!changeFilterActive}
          >
            <Tooltip title="Criteria with nothing left to judge have no change figure, so a narrowed range cannot include them. Reset the range to see them again.">
              <Typography variant="caption" color="text.secondary">
                {summaries.filter(row => row.lowestSimilarity === null).length} criteria with no
                change figure are hidden
              </Typography>
            </Tooltip>
            <IconButton
              size="small"
              onClick={() => setChangeRange([0, CHANGE_RANGE_MAX])}
              aria-label="Reset the change range"
            >
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Progress in labels, not versions: the distribution is skewed enough that a
            handful of decisions should move this bar a long way. */}
        {progress ? (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {recoveredShare.toFixed(1)}% of recoverable labels recovered —{' '}
              {progress.versionsJudged} of {progress.versionsRecoverable} versions judged
            </Typography>
            <LinearProgress variant="determinate" value={Math.min(recoveredShare, 100)} />
          </Box>
        ) : null}
      </Paper>

      {/* Always mounted at a fixed height, empty state included. Swapping the whole table
          for a short Alert collapsed the page, which took the window scrollbar away, widened
          the content area and moved the slider out from under the cursor mid-drag. Fixed
          height also stops the page resizing as rows filter in and out. */}
      <Paper>
        <TableContainer sx={{ height: 'calc(100vh - 360px)', minHeight: 240 }}>
          {rows.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <Alert severity="info">No criteria match that filter.</Alert>
            </Box>
          ) : (
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {COLUMNS.map(column => (
                    <TableCell key={column.key} align={column.numeric ? 'right' : 'left'}>
                      <Tooltip title={column.hint ?? ''}>
                        <TableSortLabel
                          active={sortKey === column.key}
                          direction={sortKey === column.key && descending ? 'desc' : 'asc'}
                          onClick={() => toggleSort(column.key)}
                        >
                          {column.label}
                        </TableSortLabel>
                      </Tooltip>
                    </TableCell>
                  ))}
                  <TableCell>Latest version</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(row => {
                  const nothingToJudge = row.versionsToJudge === 0 && row.staleCount === 0;
                  // Without this, pasting a policy UUID narrows to one row but leaves you no
                  // way to tell WHICH version matched -- you would have to open the criterion
                  // and hunt for it.
                  const matchedVersions = matchingVersions(row, query);
                  return (
                    <TableRow
                      key={row.criteriaId}
                      hover
                      onClick={() => onOpen(row.criteriaId)}
                      sx={{ cursor: 'pointer', opacity: nothingToJudge ? 0.62 : 1 }}
                    >
                      <TableCell sx={{ maxWidth: 340 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: nothingToJudge ? 400 : 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          {row.criterionName}
                          {/* A scan aid, not a column: the index is already wide and this
                              is about marking, not the eligibility worklist it sorts by. */}
                          {row.pinnedVersion !== null ? (
                            <Tooltip
                              title={`Pinned to v${row.pinnedVersion}: Stylus marks with that version, not the current one.`}
                            >
                              <PushPinIcon
                                color="error"
                                sx={{ fontSize: 15, flexShrink: 0 }}
                                aria-label={`Pinned to v${row.pinnedVersion}`}
                              />
                            </Tooltip>
                          ) : null}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.granularity}
                          {nothingToJudge ? ' · nothing to judge' : ''}
                        </Typography>
                        {matchedVersions.length > 0 ? (
                          <Tooltip title="Your search matched this version's policy UUID">
                            <Chip
                              size="small"
                              color="info"
                              variant="outlined"
                              label={`matches ${matchedVersions.map(v => `v${v}`).join(', ')}`}
                              sx={{ ml: 0.5, height: 18, fontSize: 11 }}
                            />
                          </Tooltip>
                        ) : null}
                        {row.allDisregarded ? (
                          <Tooltip title="Every version of this criterion, current included, is marked ineligible -- none of its labels are used as eval ground truth.">
                            <Chip
                              size="small"
                              color="error"
                              variant="outlined"
                              label="All ignored"
                              sx={{ ml: 0.5, height: 18, fontSize: 11 }}
                            />
                          </Tooltip>
                        ) : null}
                      </TableCell>
                      {/* Labelled out of total. The bare version count said little on its
                          own; as a denominator it shows how much of the lineage is inert. */}
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          <Box component="span" sx={{ fontWeight: 600 }}>
                            {row.versionsWithLabels}
                          </Box>
                          <Box component="span" sx={{ color: 'text.disabled' }}>
                            /{row.versionsTotal}
                          </Box>
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {row.versionsToJudge > 0 ? (
                          <Chip
                            size="small"
                            label={row.versionsToJudge}
                            color="primary"
                            variant="outlined"
                          />
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      {/* At stake out of total. The total alone said little; as a
                          denominator it shows how much of the criterion is already safe on
                          the current version. */}
                      <TableCell align="right" sx={{ minWidth: 120 }}>
                        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          <Box component="span" sx={{ fontWeight: 600 }}>
                            {row.labelsAtStake.toLocaleString()}
                          </Box>
                          <Box component="span" sx={{ color: 'text.disabled' }}>
                            /{row.labelsTotal.toLocaleString()}
                          </Box>
                        </Typography>
                        <Box sx={{ height: 3, bgcolor: 'grey.200', borderRadius: 2, mt: 0.25 }}>
                          <Box
                            sx={{
                              width: `${(row.labelsAtStake / maxAtStake) * 100}%`,
                              height: '100%',
                              bgcolor: 'primary.main',
                              borderRadius: 2,
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {row.labelsTotal.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ minWidth: 118 }}>
                        <ChangeCell
                          lowestSimilarity={row.lowestSimilarity}
                          scored={row.unjudgedScored}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(row.latestVersionDate)}
                        </Typography>
                        {row.staleCount > 0 ? (
                          <Tooltip title="A newer version has arrived since these decisions were made. Nothing has been revoked — the labels are still eligible. Open the criterion to see what changed before re-affirming.">
                            <Chip
                              size="small"
                              color="warning"
                              label={`${row.staleCount} stale`}
                              sx={{ ml: 0.5 }}
                            />
                          </Tooltip>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default CriteriaIndex;
